import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useHistory, useParams } from '@compat/router';
import PageLayout from '../../PageLayout';
import SuccessModalWithTimer from '@components/Modals/SuccessModalWithTimer';
import { CommentsBlock, RecipeCard } from '@ui';
import Breadcrumbs from '@components/Breadcrumbs';
import RecipesCarousel from '@components/Carousel/Recipes';
import { notifications } from '@utils/notifications';
import { observer } from 'mobx-react';
import scrollTo from '@utils/scrollTo';
import isEmpty from 'lodash/isEmpty';
import {
  recipeByIdQueryOptions,
  setRecipeVotedRatingInCache,
  useRecipesPopularQuery,
  useToggleRecipeFavoriteMutation
} from '@queries/recipes';
import type {
  GetRecipeByIdQuery,
  GetRecipeCommentsByRecipeIdQuery,
  GetRecipesPopularQuery
} from '@schema/cart';
import { CommentsTypeTypeEnum } from '@schema/cart';
import useWindowSize from '@customHooks/useWindowSize';
import type { AddProductToCartMutation } from '@schema/main';
import { useStores } from '@root/src/stores';
import {
  getRutubeVideoApiUrl,
  getVideoContentUrl,
  getVideoEmbedUrl,
  getVideoThumbnailUrl,
  parseVideoUrl
} from '@components/VideoPlayer/parseVideoUrl';
import { useRutubeVideoMetaQuery } from '@queries/rutubeVideo';
import { absoluteUrl } from '@constants/site';
import JsonLd from '@utils/jsonLd/JsonLd';
import { buildRecipe } from '@utils/jsonLd/recipe';
import { buildVideoObject } from '@utils/jsonLd/video';

/**
 * ВАРИАНТЫ СТРАНИЦЫ И БЛОК КОММЕНТАРИЕВ — СТАТИЧЕСКИМИ ИМПОРТАМИ, НЕ
 * `React.lazy`. При гидрации React не может «дождаться» ленивый компонент:
 * если граница Suspense подвисает, серверная разметка внутри неё
 * ВЫБРАСЫВАЕТСЯ и подставляется fallback (у PageLayout он `null`). Отсюда и
 * был эффект: SSR отдавал страницу целиком, на гидрации она схлопывалась в
 * пустоту и разворачивалась обратно, когда доезжал чанк.
 *
 * Экономии от разбиения тут всё равно не было: один из вариантов нужен
 * всегда, а `@ui` этот файл и так тянет статически (`RecipeCard`), то есть
 * `CommentsBlock` уезжал в тот же чанк. Остальные страницы с `PageLayout`
 * (Recipes, ChefBlog, ProductPage, SearchPage, CatalogCategory) с самого
 * начала импортируют свои варианты статически — приводим рецепт к ним.
 */
import RecipeDesktop from './desktop';
import RecipeLaptop from './laptop';
import RecipeMobile from './mobile';

type RecipeT = NonNullable<GetRecipeByIdQuery['getRecipeById']> | null;

export type RecipeCommentsResolverT = (
  page: number,
  setLimit?: number
) => Promise<GetRecipeCommentsByRecipeIdQuery['getRecipeCommentsByRecipeId']>;

interface RecipeState {
  recipe: RecipeT | null;
  recipesPopular: NonNullable<GetRecipesPopularQuery['getRecipesPopular']>;
  showCompleteModal: boolean;
  isDisabledPopup: boolean;
  isFavorite: boolean;
}

export type SettingsRecipeT = RecipeState & {
  isWithoutPhotos: boolean;
  isDesktop?: boolean;
  isTablet?: boolean;
  callback: () => void;
  addProductToCart: (product) => () => void;
  addAllProductToCart: () => void;
  handleRecipeFavorites: (isFavorite: boolean) => () => void;
  scrollToCommentsBlock: () => void;
  toggleLeaveRecipeFeedbackModal: () => void;
};

const useRecipeParsedId = (): {
  id: number;
} => {
  const history = useHistory();
  const { id: paramsId } = useParams<{
    id: string;
  }>();

  const getId = (newId: string): number => {
    if (newId.includes('-')) {
      newId = newId.split('-').pop() ?? '';
    }
    if (isNaN(+newId)) {
      history.replace('/404');
    }
    return +newId;
  };

  return {
    id: getId(paramsId)
  };
};

const RecipeComp: FC = observer(() => {
  const { cartStore, modalsStore, userStore } = useStores();
  const { isDesktop, isTablet } = useWindowSize();
  const history = useHistory();
  const pathname = history.location.pathname;
  const { id } = useRecipeParsedId();
  const queryClient = useQueryClient();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isShowCompleteModal, setIsShowCompleteModal] = useState(false);
  const [isDisabledPopup, setIsDisabledPopup] = useState(false);

  const { data: recipeData } = useQuery({
    ...recipeByIdQueryOptions(id),
    enabled: Number.isFinite(id)
  });
  const recipe: RecipeT = recipeData ?? null;

  const { data: recipesPopular = [] } = useRecipesPopularQuery({
    excludeIds: [id]
  });

  const commentsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollTo.params({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    if (isDisabledPopup) {
      setIsDisabledPopup(false);
    }
  }, [isDisabledPopup]);

  const { mutate: toggleFavoriteMutate } = useToggleRecipeFavoriteMutation();

  const handleRecipeFavorites = isFavorite => () => {
    const { user } = userStore;
    const { changeAuthModalState } = modalsStore;

    if (isEmpty(user)) {
      changeAuthModalState();
      return;
    }
    if (!isFavorite && !id) return;

    toggleFavoriteMutate(
      { recipeId: Number(id), isFavorite },
      {
        onSuccess: () => {
          setIsFavorite(!isFavorite);
        }
      }
    );
    notifications(
      isFavorite
        ? 'Рецент успешно удален из избранного'
        : 'Рецепт успешно добавлен в избранное',
      ''
    );
  };

  const _commentsResolver: RecipeCommentsResolverT = async (page, setLimit) => {
    const getRecipeCommentsByRecipeId: GetRecipeCommentsByRecipeIdQuery['getRecipeCommentsByRecipeId'] =
      await cartStore
        .withCartTokenSpread(
          { id: `${id}`, page, setLimit },
          'getRecipeCommentsByRecipeId'
        )
        .then(res => res.getRecipeCommentsByRecipeId);
    return getRecipeCommentsByRecipeId;
  };

  const showCompleteModal = (): void => {
    setIsShowCompleteModal(true);
    setIsDisabledPopup(true);
  };

  const changeCompleteModalState = (): void => {
    setIsShowCompleteModal(false);
  };

  const addProductToCart = product => () => {
    // ПОСМОТРЕТЬ
    const id = product?.catalogItems?.id;
    const { productsAmount, updateCart, changeProductsAmount } = cartStore;
    changeProductsAmount(1, id);
    updateCart(id, productsAmount[id]);
  };

  const addAllProductToCart = (): void => {
    const { getCart, getUpdatedCart } = cartStore;
    const recipeIngredients = recipe?.recipeIngredients;
    if (!recipeIngredients?.length) return;

    const listWait: Array<Promise<AddProductToCartMutation>> = [];

    recipeIngredients.forEach(item => {
      if (item?.ingredientsItems?.[0]?.catalogItem?.available) {
        const id = item.ingredientsItems[0].catalogItem.id;

        listWait.push(
          getUpdatedCart({
            productId: Number(id),
            quantity: 1
          })
        );
      }
    });

    Promise.all(listWait)
      .then(() => {
        getCart({});
        notifications('Все ингредиенты добавлены в Вашу корзину', 'Рецепт');
      })
      .catch(err => {
        console.log('Error: ', err);
        getCart({});
        notifications('Не все ингредиенты добавлены в Вашу корзину', 'Рецепт');
      });
  };

  const scrollToCommentsBlock = (): void => {
    if (commentsRef?.current) {
      commentsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const toggleLeaveRecipeFeedbackModal = (): void => {
    modalsStore.openLeaveFeedback({
      isRateRecipe: true,
      modalTitle: 'Оцените рецепт',
      recipeId: String(id),
      getRecipeVotedRating: setRecipeVotedRating
    });
  };

  const setRecipeVotedRating = (rating: number): void => {
    if (!recipe) return;
    setRecipeVotedRatingInCache(queryClient, id, rating);
  };

  const breadCrumbsArray = [
    { title: 'Главная', url: '/' },
    { title: 'Блог шеф-повара', url: '/cook' },
    { title: 'Рецепты', url: '/recipes' },
    { title: recipe?.title ?? '', url: pathname ?? '' }
  ];

  const isWithoutPhotos =
    !!recipe?.is_product_promo ||
    !!recipe?.recipeInstructions?.every(
      item =>
        !item?.mainImage || item?.mainImage?.includes('productCardErrorImg')
    );

  const videoCode = recipe?.video_code;
  const parsedVideo = useMemo(
    () => (videoCode ? parseVideoUrl(videoCode) : null),
    [videoCode]
  );
  const rutubeMeta = useRutubeVideoMetaQuery(
    parsedVideo ? getRutubeVideoApiUrl(parsedVideo) : null
  );
  const recipeJsonLd = useMemo(() => {
    const embedUrl = parsedVideo ? getVideoEmbedUrl(parsedVideo) : null;
    const video = embedUrl
      ? buildVideoObject({
          name: recipe?.title
            ? `${recipe.title} — видео рецепт приготовления`
            : null,
          description: recipe?.text,
          thumbnailUrls: [
            parsedVideo ? getVideoThumbnailUrl(parsedVideo) : null,
            rutubeMeta.thumbnailUrl,
            recipe?.attachmentsItem?.cacheItem?.[0]?.filePath
          ],
          contentUrl: parsedVideo ? getVideoContentUrl(parsedVideo) : null,
          embedUrl,
          uploadDate: rutubeMeta.uploadDate ?? recipe?.date,
          duration: rutubeMeta.duration
        })
      : null;
    return buildRecipe(recipe, { url: absoluteUrl(pathname), video });
  }, [recipe, parsedVideo, rutubeMeta, pathname]);

  const settings: SettingsRecipeT = {
    isDisabledPopup,
    isFavorite,
    recipe,
    recipesPopular,
    showCompleteModal: isShowCompleteModal,
    isDesktop,
    isTablet,
    isWithoutPhotos,
    callback: showCompleteModal,
    addProductToCart,
    addAllProductToCart,
    handleRecipeFavorites,
    scrollToCommentsBlock,
    toggleLeaveRecipeFeedbackModal
  };

  return (
    <div className='recipe-page'>
      {isShowCompleteModal && (
        <SuccessModalWithTimer
          closeModal={changeCompleteModalState}
          title={'Вы подписались на рассылку'}
        />
      )}

      <div className='wrapper-container'>
        <JsonLd data={recipeJsonLd} />
        <Breadcrumbs noPaddingMobile array={breadCrumbsArray} />

        <PageLayout
          renderDesktop={RecipeDesktop}
          settingDesktop={settings}
          renderLaptop={RecipeLaptop}
          settingLaptop={settings}
          renderMobile={RecipeMobile}
          settingMobile={settings}
        />
      </div>
      <div
        ref={commentsRef}
        className='wrapper-container grid grid-cols-[66%,335px] allMd:flex allMd:flex-col allMd:gap-x-6 justify-between mb-10'
      >
        {recipe?.id && (
          <CommentsBlock
            title='Оставить комментарий'
            commentsItemsName='recipeCommentsItem'
            resolver={_commentsResolver}
            addCommentInstance='Recipes'
            objectId={Number(recipe.id)}
            newTypeComment
            commentType={CommentsTypeTypeEnum.Comment}
          />
        )}

        <div className='allMd:hidden'>
          <div className='flex items-center justify-between mb-6'>
            <span className='text-xl font-medium'> Попробуйте приготовить</span>
            <Link
              to='/recipes'
              className='underline text-[var(--dark-red)] transition-colors hover-[var(--main-red)]'
            >
              Еще
            </Link>
          </div>

          {recipesPopular?.[0] && (
            <div className='articles-recipes'>
              <ul>
                <RecipeCard item={recipesPopular[0]} />
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className='wrapper-container allMd:p-0'>
        <RecipesCarousel recipes={recipesPopular} />
      </div>
    </div>
  );
});

export default RecipeComp;
